import React from 'react';
import { Header } from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import {
    User,
    GraduationCap,
    Briefcase,
    Plane,
    Heart,
    Globe,
    Shield,
    Zap,
    Clock,
    CheckCircle,
    FileText,
    Mail,
    Phone,
    MapPin,
    ArrowRight,
    Star,
    Sparkles,
    BookOpen,
    CreditCard,
    Calendar,
    Award,
    Target,
    MessageSquare
} from 'lucide-react';

const PersonalVisaPage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();

    const handleHome = () => {
        navigate('/');
    };

    const handleNewChat = () => {
        navigate('/');
    };

    // 签证类型
    const visaTypes = [
        {
            icon: <GraduationCap className="w-8 h-8" />,
            name: t('学生签证', 'Student Visa'),
            description: t('留学英国，攻读学位或短期课程', 'Study in the UK for a degree or short course.'),
            requirements: ['CAS', t('资金证明', 'Financial evidence'), t('英语成绩', 'English score')],
            processingTime: t('3周', '3 weeks'),
            validity: t('课程长度+4个月', 'Course length + 4 months'),
            color: 'from-blue-500 to-cyan-500',
            popular: true
        },
        {
            icon: <Plane className="w-8 h-8" />,
            name: t('旅游签证', 'Visitor Visa'),
            description: t('旅游、探亲、商务访问', 'Tourism, family visits, and business visits.'),
            requirements: [t('行程计划', 'Travel itinerary'), t('资金证明', 'Financial evidence'), t('在职证明', 'Employment letter')],
            processingTime: t('3周', '3 weeks'),
            validity: t('6个月/2年/5年/10年', '6 months / 2 / 5 / 10 years'),
            color: 'from-green-500 to-emerald-500',
            popular: true
        },
        {
            icon: <Briefcase className="w-8 h-8" />,
            name: t('工作签证', 'Work Visa'),
            description: t('技术工人、创新者、全球人才', 'Skilled Worker, Innovator, and Global Talent routes.'),
            requirements: [t('工作邀请', 'Job offer'), t('英语B1', 'English B1'), t('薪资要求', 'Salary requirement')],
            processingTime: t('3-8周', '3-8 weeks'),
            validity: t('最长5年', 'Up to 5 years'),
            color: 'from-purple-500 to-pink-500',
            popular: true
        },
        {
            icon: <Heart className="w-8 h-8" />,
            name: t('家庭签证', 'Family Visa'),
            description: t('配偶、未婚伴侣、家属陪同', 'Spouse, unmarried partner, and dependants.'),
            requirements: [t('关系证明', 'Relationship evidence'), t('资金证明', 'Financial evidence'), t('英语A1', 'English A1')],
            processingTime: t('8-12周', '8-12 weeks'),
            validity: t('2.5年', '2.5 years'),
            color: 'from-red-500 to-orange-500',
            popular: false
        },
        {
            icon: <Globe className="w-8 h-8" />,
            name: t('投资签证', 'Business Visa'),
            description: t('投资者、初创签证', 'Investor and start-up related routes.'),
            requirements: [t('投资金额', 'Investment amount'), t('商业计划', 'Business plan'), t('资金来源', 'Source of funds')],
            processingTime: t('3-4周', '3-4 weeks'),
            validity: t('3年', '3 years'),
            color: 'from-yellow-500 to-amber-500',
            popular: false
        },
        {
            icon: <Award className="w-8 h-8" />,
            name: t('永居入籍', 'Settlement & Citizenship'),
            description: t('申请永久居留或英国国籍', 'Apply for indefinite leave to remain or British citizenship.'),
            requirements: [t('居住时间', 'Residence period'), t('语言考试', 'Language test'), 'Life in UK'],
            processingTime: t('6个月', '6 months'),
            validity: t('永久', 'Permanent'),
            color: 'from-indigo-500 to-indigo-600',
            popular: false
        }
    ];

    // 服务流程
    const processSteps = [
        {
            step: 1,
            title: t('在线咨询', 'Online consultation'),
            description: t('通过AI助手或人工客服了解签证要求', 'Understand visa requirements through the AI assistant or human support.'),
            icon: <MessageSquare className="w-6 h-6" />
        },
        {
            step: 2,
            title: t('材料准备', 'Prepare documents'),
            description: t('获取个性化材料清单，准备申请文件', 'Get a personalised checklist and prepare your application files.'),
            icon: <FileText className="w-6 h-6" />
        },
        {
            step: 3,
            title: t('在线申请', 'Apply online'),
            description: t('填写申请表，预约生物信息采集', 'Complete the form and book a biometrics appointment.'),
            icon: <Globe className="w-6 h-6" />
        },
        {
            step: 4,
            title: t('进度追踪', 'Track progress'),
            description: t('实时查询申请进度，接收通知提醒', 'Track status and receive reminders.'),
            icon: <Clock className="w-6 h-6" />
        }
    ];

    // 成功案例
    const successCases = [
        {
            name: t('王小明', 'Xiaoming Wang'),
            visaType: t('学生签证', 'Student Visa'),
            university: t('伦敦大学学院', 'University College London'),
            quote: t('AI助手帮我准备了完整的材料清单，申请过程特别顺利！', 'The AI assistant prepared a complete document checklist and made the process smooth.'),
            avatar: '👨‍🎓',
            rating: 5
        },
        {
            name: t('李芳', 'Fang Li'),
            visaType: t('旅游签证', 'Visitor Visa'),
            purpose: t('探亲访友', 'Visiting family and friends'),
            quote: t('第一次申请英国签证，很多地方不懂。AI助手24小时在线解答，太方便了！', 'It was my first UK visa application, and the 24/7 AI answers were very helpful.'),
            avatar: '👩‍💼',
            rating: 5
        },
        {
            name: t('张伟', 'Wei Zhang'),
            visaType: t('工作签证', 'Work Visa'),
            company: t('某科技公司', 'A technology company'),
            quote: t('从签证选择到材料准备，AI都给出了详细指导。成功拿到技术工签！', 'From route selection to documents, the AI gave detailed guidance. I got my Skilled Worker visa.'),
            avatar: '👨‍💻',
            rating: 5
        }
    ];

    // 常见问题
    const faqs = [
        {
            question: t('签证申请需要多长时间？', 'How long does a visa application take?'),
            answer: t('不同类型的签证处理时间不同：旅游签证约3周，学生签证约3周，工作签证3-8周。', 'Processing times vary: Visitor and Student visas are usually around 3 weeks, while Work visas often take 3-8 weeks.')
        },
        {
            question: t('资金证明有什么要求？', 'What are the financial evidence requirements?'),
            answer: t('资金需存满28天，金额需覆盖学费和生活费。伦敦地区生活费每月£1,334，非伦敦地区£1,023。', 'Funds usually need to be held for 28 days and cover tuition and living costs. Living costs are £1,334/month in London and £1,023/month outside London.')
        },
        {
            question: t('被拒签了怎么办？', 'What should I do after a refusal?'),
            answer: t('仔细阅读拒签信了解原因，收集新证据重新申请，或考虑上诉。', 'Read the refusal letter carefully, gather new evidence, reapply, or consider an appeal if available.')
        },
        {
            question: t('可以加急处理吗？', 'Can I use priority processing?'),
            answer: t('可以，优先签证5个工作日，超级优先24小时，需额外付费。', 'Yes. Priority service is usually 5 working days and super priority can be 24 hours, with additional fees.')
        }
    ];

    return (
        <div className="min-h-screen bg-white dark:bg-[#0f172a]">
            <Header onClearChat={handleNewChat} onHome={handleHome} showNewChat={true} />

            {/* Hero 区域 */}
            <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 dark:from-indigo-900 dark:via-indigo-800 dark:to-indigo-900 text-white">
                <div className="absolute inset-0 bg-black opacity-10"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-pink-600/20"></div>

                <div className="relative max-w-6xl mx-auto px-4 py-20 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                            <User className="w-4 h-4" />
                            <span className="text-sm font-medium">{t('个人签证', 'Personal Visa')}</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                            {t('个人签证申请', 'Personal Visa Applications')}
                        </h1>
                        <p className="text-xl mb-8 text-indigo-100">
                            {t('为学生、游客、工作者提供专业的签证申请指导', 'Professional visa application guidance for students, visitors, and workers.')}
                        </p>

                        {/* 快速咨询区域 */}
                        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 sm:p-8">
                            <div className="flex flex-col items-center gap-4">
                                <div className="flex items-center gap-2 text-xl font-semibold">
                                    <Sparkles className="w-5 h-5" />
                                    <span>{t('快速咨询签证问题', 'Ask a visa question')}</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
                                    {[
                                        t('学生签证需要什么材料？', 'What documents are needed for a Student Visa?'),
                                        t('旅游签证费用多少？', 'How much does a Visitor Visa cost?'),
                                        t('工作签证英语要求', 'English requirements for a Work Visa'),
                                        t('资金证明要求', 'Financial evidence requirements')
                                    ].map((question, index) => (
                                        <button
                                            key={index}
                                            onClick={() => navigate('/')}
                                            className="flex items-center justify-between px-4 py-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-left group"
                                        >
                                            <span className="text-sm sm:text-base">{question}</span>
                                            <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </button>
                                    ))}
                                </div>

                                <button
                                    onClick={() => navigate('/')}
                                    className="mt-4 px-8 py-3 bg-white text-indigo-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl inline-flex items-center gap-2"
                                >
                                    <MessageSquare className="w-5 h-5" />
                                    {t('开始咨询', 'Start consultation')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 签证类型 */}
            <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('签证类型', 'Visa Types')}</h2>
                <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
                    {t('选择适合你的签证类型，获取详细申请指南', 'Choose the right visa type and get a detailed application guide.')}
                </p>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {visaTypes.map((visa, index) => (
                        <div
                            key={index}
                            className="relative bg-white dark:bg-[#1e293b] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all group"
                        >
                            {visa.popular && (
                                <div className="absolute top-3 right-3">
                                    <span className="bg-indigo-600 text-white text-xs px-2 py-1 rounded-full">
                                        {t('热门', 'Popular')}
                                    </span>
                                </div>
                            )}

                            <div className="p-6">
                                <div className={`w-14 h-14 bg-gradient-to-br ${visa.color} rounded-lg flex items-center justify-center mb-4 text-white`}>
                                    {visa.icon}
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{visa.name}</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{visa.description}</p>

                                <div className="space-y-3 mb-4">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-500 mb-1">{t('主要材料', 'Key documents')}</p>
                                        <div className="flex flex-wrap gap-1">
                                            {visa.requirements.map((req, i) => (
                                                <span key={i} className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                                                    {req}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-500">{t('处理时间', 'Processing')}</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{visa.processingTime}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-500">{t('有效期', 'Validity')}</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{visa.validity}</p>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => navigate('/')}
                                    className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                                >
                                    {t('查看详情', 'View details')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 服务流程 */}
            <div className="bg-gray-50 dark:bg-[#1e293b] border-y border-gray-200 dark:border-gray-800">
                <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('服务流程', 'Service Process')}</h2>
                    <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
                        {t('简单四步，轻松搞定签证申请', 'Four simple steps to handle your visa application.')}
                    </p>

                    <div className="grid md:grid-cols-4 gap-6">
                        {processSteps.map((step, index) => (
                            <div key={index} className="relative">
                                {index < processSteps.length - 1 && (
                                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-indigo-200 dark:bg-indigo-800"></div>
                                )}
                                <div className="relative flex flex-col items-center text-center">
                                    <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center mb-4 text-xl font-bold">
                                        {step.step}
                                    </div>
                                    <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mb-3 text-indigo-600 dark:text-indigo-400">
                                        {step.icon}
                                    </div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{step.title}</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 成功案例 */}
            <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('成功案例', 'Success Stories')}</h2>
                <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
                    {t('看看其他用户的签证申请经历', 'See how other users completed their visa applications.')}
                </p>

                <div className="grid md:grid-cols-3 gap-6">
                    {successCases.map((case_, index) => (
                        <div key={index} className="bg-white dark:bg-[#1e293b] rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="text-3xl">{case_.avatar}</span>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white">{case_.name}</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{case_.visaType}</p>
                                </div>
                            </div>
                            <div className="flex mb-3">
                                {[...Array(case_.rating)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                ))}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 italic">"{case_.quote}"</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* 常见问题 */}
            <div className="bg-gray-50 dark:bg-[#1e293b] border-y border-gray-200 dark:border-gray-800">
                <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">{t('常见问题', 'FAQ')}</h2>

                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div key={index} className="bg-white dark:bg-[#0f172a] rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                                <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                                    <span className="text-indigo-600 dark:text-indigo-400">Q{index + 1}.</span>
                                    {faq.question}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">{faq.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 行动号召 */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white">
                <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-bold mb-4">{t('开始你的签证申请', 'Start Your Visa Application')}</h2>
                    <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                        {t('让AI助手帮你轻松搞定英国签证', 'Let the AI assistant make your UK visa application easier.')}
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-white text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl inline-flex items-center gap-2"
                    >
                        <MessageSquare className="w-5 h-5" />
                        {t('立即咨询', 'Ask now')}
                    </button>
                </div>
            </div>

            {/* 底部版权 */}
            <footer className="bg-gray-900 text-white">
                <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
                    <div className="text-center text-sm text-gray-400">
                        <p>{t('© 2026 英国签证AI助手. 保留所有权利。', '© 2026 UK Visa AI Assistant. All rights reserved.')}</p>
                        <p className="mt-2">
                            <a href="#" className="hover:text-white transition-colors">{t('ICP证: 京ICP备12345678号', 'ICP: Beijing ICP 12345678')}</a> |
                            <a href="#" className="hover:text-white transition-colors ml-2">{t('营业执照', 'Business License')}</a> |
                            <a href="#" className="hover:text-white transition-colors ml-2">{t('隐私政策', 'Privacy Policy')}</a>
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PersonalVisaPage;
